import { useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { TAB_KEYS } from '../../../config/constants'
import pathnames from '../../../config/pathnames'
import type { Tenant } from '../../../types/tenant'
import { Breadcrumb } from '../../../ui/Breadcrumb'
import { FancyLoader } from '../../../ui/FancyLoader'
import { Tabs } from '../../../ui/Tabs'
import { TileHeader } from '../../../ui/TileHeader'

import { TenantDetails } from './TenantDetails'
import { TenantLicenses } from './TenantLicenses'
import { tenantProfileApi } from './TenantProfile.api'
import type { TenantProfileProps } from './TenantProfile.types'

export default function TenantProfile(props: TenantProfileProps) {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const alert = useAlert()
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(false)
  const { name } = props
  const [tab, setTab] = useState(TAB_KEYS.OVERVIEW)
  const location = useLocation()

  const tabItems = [
    {
      label: t('Tenants.Profile.Tabs.Overview'),
      href: pathnames.getTenantProfileTab(id ?? '', TAB_KEYS.OVERVIEW),
      active: true,
      key: TAB_KEYS.OVERVIEW,
    },
    {
      label: t('Tenants.Profile.Tabs.Licenses'),
      href: pathnames.getTenantProfileTab(id ?? '', TAB_KEYS.LICENSES),
      active: false,
      key: TAB_KEYS.LICENSES,
    },
    {
      label: t('Tenants.Profile.Tabs.Settings'),
      href: pathnames.getTenantProfileTab(id ?? '', TAB_KEYS.SETTINGS),
      active: false,
      key: TAB_KEYS.SETTINGS,
    },
  ]

  const getApi = () => {
    setLoading(true)

    tenantProfileApi
      .get(id)
      .then((tenant) => {
        setTenant(tenant || null)
      })
      .catch((err) => {
        alert.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getApi()
  }, [id])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tabFromUrl = searchParams.get('tab')
    const tabItem = tabItems.find((item) => item.key === tabFromUrl)

    if (tabItem) {
      setTab(tabItem.key)
    } else {
      setTab(TAB_KEYS.OVERVIEW)
    }
  }, [location.search, tabItems])

  const onChangeTab = (newTab: string) => {
    if (newTab === TAB_KEYS.OVERVIEW) {
      getApi()
    }

    setTab(newTab)
  }

  if (loading) {
    return <FancyLoader />
  }

  return (
    <div className="p-6">
      <Breadcrumb
        items={[
          {
            label: t('Breadcrumb.Home'),
            href: pathnames.dashboard,
          },
          {
            label: t('Breadcrumb.Tenants'),
            href: pathnames.tenants,
          },
          {
            label: tenant?.name || name,
            href: pathnames.getTenantProfile(id || ''),
          },
        ]}
      />

      <TileHeader name={tenant?.name || name} />

      <Tabs
        items={tabItems}
        activeTab={tab}
        defaultTab={TAB_KEYS.OVERVIEW}
        onTabClick={(value) => onChangeTab(value)}
      />

      {tab === TAB_KEYS.OVERVIEW && (
        <TenantDetails tenant={tenant} dataTestId="TenantDetails" />
      )}

      {tab === TAB_KEYS.LICENSES && (
        <TenantLicenses tenant={tenant} dataTestId="TenantLicenses" />
      )}

      {tab === TAB_KEYS.SETTINGS && (
        <div className="p-6 text-left text-gray-600">
          {t('Tenants.Profile.Settings.Description')}
        </div>
      )}
    </div>
  )
}
